<template>
  <div>
    <multiselect
      ref="multiselect"
      v-model="selectedOption"
      tag-placeholder=""
      placeholder=""
      track-by="value"
      label="text"
      :options="options"
      :loading="isLoading"
      :multiple="config.multiple"
      :limit="config.limit"
      :taggable="false"
      @search-change="onSearchChanged">
    </multiselect>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';

const DROPDOWN_SELECTOR = '.multiselect__content-wrapper';
const DEFAULT_SCROLL_PAGE_SIZE = 200;
export default {
  name: 'ClientMultiSelect',
  components: {
    Multiselect,
  },
  props: {
    config: {
      type: Object,
      default: () => ({ multiple: false, limit: 3 }),
    },
    filters: {
      type: Object,
      required: false,
    },
    selectedValue: {
      type: [ Array, Object ],
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      selectedOption: [],
      lastScrollTop: 0,
      paging: {
        page: 1,
        limit: 25,
      },
      items: [],
      total: 0,
    };
  },
  computed: {
    options() {
      return this.items;
    },
  },
  async mounted() {
    const elements = this.$refs.multiselect.$el.querySelector(DROPDOWN_SELECTOR);
    if (elements) {
      elements.addEventListener('scroll', this.onScroll);
    }

    this.selectedOption = this.selectedValue;
    await this.fetchData();
  },
  beforeDestroy() {
    this.$refs.multiselect.$el.querySelector(DROPDOWN_SELECTOR).removeEventListener('scroll', this.onScroll);
  },
  methods: {
    async fetchData(searchText) {
      this.isLoading = true;

      try {
        const offset = (this.paging.page - 1) * this.paging.limit;

        if (offset > 0 && offset > this.total) {
          return;
        }

        let params = {
          offset,
          limit: this.paging.limit,
        };

        if (searchText) {
          params.search = searchText;
        }

        params = this.applyFilters(params);
        const res = await this.$store.dispatch('Onboarding/fetchClients', { params });
        if (!res) {
          return;
        }

        this.total = res.total;
        const items = res.items.map(c => ({ value: c.id, text: this.textFormatter(c) }));

        this.items = this.mergeItems(items);
      } catch (e) {
        this.$noty.error('An error occurred while fetching clients');
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    mergeItems(data) {
      if (!this.items || this.items.length === 0) {
        return data;
      }

      const existingKeysSet = new Set(this.items.map(item => item.value));

      const newItems = data.filter(item => !existingKeysSet.has(item.value));

      return [ ...this.items, ...newItems ];
    },
    applyFilters(params) {
      if (!this.filters) {
        return params;
      }

      Object.entries(this.filters).forEach(([ paramName, paramValue ]) => {
        params[paramName] = paramValue;
      });

      return params;
    },
    onSearchChanged(query) {
      this.fetchData(query);
    },
    onScroll() {
      const dropdown = this.$refs.multiselect.$el.querySelector(DROPDOWN_SELECTOR);
      const currentScrollTop = dropdown.scrollTop;

      // We need about 700 pixels to display 25 items, our paging limit value.
      // Because we don't control the user scroll down speed we are fetching data before reaching the pixels limit.
      const nextScrollTop = (this.paging.page * DEFAULT_SCROLL_PAGE_SIZE);

      if (currentScrollTop > this.lastScrollTop && currentScrollTop > nextScrollTop) {
        this.paging.page++;
        this.lastScrollTop = currentScrollTop;

        this.fetchData();
      }
    },
    textFormatter(item) {
      return `${item.display_name} (ID: ${item.id})`;
    },
  },
  watch: {
    selectedOption: {
      handler(newValue) {
        this.$emit('selection-change', newValue);
      },
    },
  },
};
</script>

<style lang="scss">
@import '../../styles/OrderManagement.scss';
</style>
